<template>
  <CardWithTitle
    :item-id="itemId"
    :request-status="requestStatus"
    :options-data="cardOptionsWithCSVExportOption"
    @call-option-menu="callOptionMenu"
    @reload="$emit('reload', $event)"
  >
    <v-row
      no-gutters
      class="line-chart-container"
    >
      <slot />
      <HighCharts
        v-if="series.length"
        :ref="chartRef"
        class="line-chart"
        :options="chartOptions"
        :callback="highchartsCallBack"
      />
      <ChartLegendTable
        :legend-data="localizedLegendData"
        :wrap-labels="wrapLegendLabels"
      />
    </v-row>

    <template #top-right-slot>
      <GranularityDropDown
        v-if="showGranularity"
        v-model="granularity"
        :item-id="itemId"
        @reload="$emit('reload', $event)"
      />

      <Badge
        v-if="hasFilterApplied && isEditor"
        type="info"
        :tooltip="$t('components.Badge.label.tooltip.info')"
        :value="$t('components.Badge.label.icon.key')"
        :preset="true"
      />
    </template>
  </CardWithTitle>
</template>

<script>
import { Chart } from 'highcharts-vue';
import highChartsBasics from '@/mixins/highcharts-basics';
import exportModel from '@/mixins/export-model';
import cardsModel from '@/mixins/cards-model';
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import merge from 'lodash/merge';
import GranularityDropDown from '@/components/GranularityDropDown';
import Badge from '@/components/Badge';
import ChartLegendTable from '@/components/cards/models/ChartLegendTable';

export default {
  name: 'LineChart',
  components: {
    HighCharts: Chart,
    CardWithTitle,
    GranularityDropDown,
    Badge,
    ChartLegendTable,
  },
  mixins: [
    highChartsBasics,
    cardsModel,
    exportModel,
  ],
  props: {
    requestStatus: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    series: {
      type: [Array, Object],
      default: () => ([]),
    },
    categories: {
      type: Array,
      default: () => ([]),
    },
    cardOptions: {
      type: [Array, Object],
      default: () => ([]),
    },
    showGranularity: {
      type: Boolean,
      default: true,
    },
    wrapLegendLabels: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      granularity: null,
      legendData: null,
    };
  },
  computed: {
    localizedLegendData() {
      if (!this.legendData) return null;
      return this.legendData.map((hcSeries, i) => {
        const propSeries = Array.isArray(this.series) ? this.series[i] : null;
        return {
          color: hcSeries.color,
          visible: hcSeries.visible,
          name: propSeries ? propSeries.name : hcSeries.name,
          setVisible: hcSeries.setVisible ? hcSeries.setVisible.bind(hcSeries) : undefined,
          setState: hcSeries.setState ? hcSeries.setState.bind(hcSeries) : undefined,
        };
      });
    },
    chartOptions() {
      const customOptions = {
        chart: {
          type: 'line',
          plotBackgroundColor: null,
          plotBorderWidth: 0,
          plotShadow: false,
        },
        plotOptions: {
          line: {
            borderWidth: 0.5,
          },
        },
        tooltip: {
          shared: true,
          xDateFormat: this.xDateFormat,
        },
        xAxis: {
          lineWidth: 0,
          tickLength: 0,
          type: this.typeXAxios,
          title: null,
          labels: {
            format: this.labelFormat,
          },
        },
        yAxis: {
          gridLineWidth: 0,
          title: this.$t('components.models.LineChart.label.yAxisTitle'),
        },
        series: this.series,
      };

      return merge({}, this.highchartsBasics, this.highchartTheme, customOptions, this.cardOptions, { legend: { enabled: false } });
    },
  },
  watch: {
    chartOptions() {
      this.$nextTick(() => {
        this.highchartsCallBack(this.$refs[this.chartRef]?.chart);
      });
    },
  },
  methods: {
    highchartsCallBack(chart) {
      this.legendData = chart?.series;
    },
  },
};
</script>

<style scoped lang="scss">
  .line-chart-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow: hidden;
    justify-content: flex-start;
  }

  .line-chart {
    width: 100%;
    height: calc(100% - 72px);
  }
</style>
